import { useCallback, useEffect, useMemo, useState } from "react"

import type {
  CustomerToCashQueueV1,
  CustomerToCashReportV1,
} from "@impetusos/contracts"

import { ConsoleRequestError, readCustomerToCashReport } from "../transport/index.js"

const queues: readonly { id: CustomerToCashQueueV1; label: string }[] = [
  { id: "stuck_commands", label: "Stuck commands" },
  { id: "unmatched_provider_events", label: "Unmatched events" },
  { id: "payment_exceptions", label: "Payment exceptions" },
  { id: "communication_exceptions", label: "Communication exceptions" },
  { id: "invoice_aging", label: "Invoice aging" },
  { id: "audit_history", label: "Audit history" },
]

const reportDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "America/New_York",
})

const formatTime = (value: string) => reportDateFormatter.format(new Date(value))

const formatMoney = (amount: number, currency: string) =>
  `${currency} ${(amount / 100).toFixed(2)}`

export const CustomerToCashWorkspace = () => {
  const [queue, setQueue] = useState<CustomerToCashQueueV1>("stuck_commands")
  const [legalEntityId, setLegalEntityId] = useState<string | null>(null)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [cursor, setCursor] = useState(0)
  const [history, setHistory] = useState<number[]>([])
  const [report, setReport] = useState<CustomerToCashReportV1 | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")

  const load = useCallback(async () => {
    setState("loading")
    try {
      setReport(await readCustomerToCashReport({
        reportVersion: 1,
        queue,
        legalEntityId,
        branchId,
        cursor,
        pageSize: 10,
      }))
      setState("ready")
    } catch (error) {
      if (error instanceof ConsoleRequestError) setState("error")
      else setState("error")
    }
  }, [branchId, cursor, legalEntityId, queue])

  useEffect(() => { void load() }, [load])

  const branches = useMemo(
    () => report?.filters.branches.filter(
      (option) => legalEntityId === null || option.legalEntityId === legalEntityId
    ) ?? [],
    [legalEntityId, report]
  )

  const changeQueue = (next: CustomerToCashQueueV1) => {
    setQueue(next)
    setCursor(0)
    setHistory([])
  }

  const selectLegalEntity = (value: string) => {
    setLegalEntityId(value || null)
    setBranchId(null)
    setCursor(0)
    setHistory([])
  }

  const selectBranch = (value: string) => {
    setBranchId(value || null)
    setCursor(0)
    setHistory([])
  }

  return (
    <section className="report-workspace" id="customer-to-cash" aria-labelledby="report-title">
      <header className="report-header">
        <div>
          <p className="eyebrow">Customer-to-cash control desk</p>
          <h2 id="report-title">Exceptions and aging</h2>
          <p className="muted">A Tenant-scoped view of work that needs attention and its human audit trail.</p>
        </div>
        <div className="report-filters" aria-label="Report filters">
          <label>
            <span>Legal Entity</span>
            <select value={legalEntityId ?? ""} onChange={(event) => selectLegalEntity(event.target.value)}>
              <option value="">All Legal Entities</option>
              {report?.filters.legalEntities.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Branch</span>
            <select value={branchId ?? ""} onChange={(event) => selectBranch(event.target.value)}>
              <option value="">All Branches</option>
              {branches.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <div className="report-summary" aria-label="Open operational items">
        <div><strong>{report?.summary.stuckCommands ?? "—"}</strong><span>Stuck commands</span></div>
        <div><strong>{report?.summary.unmatchedProviderEvents ?? "—"}</strong><span>Unmatched events</span></div>
        <div><strong>{report?.summary.paymentExceptions ?? "—"}</strong><span>Payment exceptions</span></div>
        <div><strong>{report?.summary.communicationExceptions ?? "—"}</strong><span>Communication exceptions</span></div>
        <div><strong>{report?.summary.overdueInvoices ?? "—"}</strong><span>Overdue invoices</span></div>
      </div>

      <div className="report-tabs" role="tablist" aria-label="Report queue">
        {queues.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            role="tab"
            aria-selected={queue === candidate.id}
            className={queue === candidate.id ? "active" : ""}
            onClick={() => changeQueue(candidate.id)}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      <div className="report-table-wrap" role="tabpanel" aria-live="polite">
        {state === "loading" && <div className="report-message"><span className="spinner" />Reading Frappe…</div>}
        {state === "error" && (
          <div className="report-message" role="alert">
            The authoritative report is unavailable.
            <button type="button" className="text-button" onClick={() => { void load() }}>Retry</button>
          </div>
        )}
        {state === "ready" && report?.page.items.length === 0 && (
          <div className="report-message report-clear"><span aria-hidden="true">✓</span>No items in this view.</div>
        )}
        {state === "ready" && report && report.page.items.length > 0 && (
          <table className="report-table">
            <caption className="sr-only">{queues.find((candidate) => candidate.id === queue)?.label}</caption>
            <thead><tr><th scope="col">Item</th><th scope="col">Status</th><th scope="col">When</th><th scope="col">Scope</th><th scope="col">Detail</th></tr></thead>
            <tbody>
              {report.page.items.map((item) => (
                <tr key={item.itemId}>
                  <td><strong>{item.title.replaceAll("_", " ")}</strong>{item.primaryRef && <span>{item.primaryRef}</span>}</td>
                  <td><span className="report-status">{item.status.replaceAll("_", " ")}</span></td>
                  <td>{formatTime(item.occurredAt)}</td>
                  <td>{item.branchId ?? item.legalEntityId ?? "Tenant-wide"}</td>
                  <td><span>{item.detailCode.replaceAll("_", " ")}</span>{item.amountMinor !== null && item.currency !== null && <strong>{formatMoney(item.amountMinor, item.currency)}</strong>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <footer className="report-footer">
        <span>{report ? `Observed ${formatTime(report.observedAt)}` : "Awaiting authoritative source"}</span>
        <div>
          <button
            type="button"
            className="secondary-button"
            disabled={history.length === 0 || state === "loading"}
            onClick={() => {
              const previous = history.at(-1)
              if (previous === undefined) return
              setHistory((values) => values.slice(0, -1))
              setCursor(previous)
            }}
          >
            Previous
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={report?.page.nextCursor === null || report === null || state === "loading"}
            onClick={() => {
              if (report?.page.nextCursor === null || report === null) return
              setHistory((values) => [...values, cursor])
              setCursor(report.page.nextCursor)
            }}
          >
            Next
          </button>
        </div>
      </footer>
    </section>
  )
}
